import { z } from 'zod';
import { AxonautReadState, SubcontractingSettings } from './subcontracting.js';
import {
  DEFAULT_INTEGRATION_WEEKS,
  DEFAULT_MEETING_WINDOW_DAYS,
  DEFAULT_MOCKUPS_PER_WEEK,
  DEFAULT_PARTNER_INTEGRATION_DELAY_DAYS,
  DEFAULT_R3_TO_MOCKUP_MAX_WEEKS,
} from '../planning.js';
import { findTeamMember, TEAM_HINTS, type TeamKey, type TeamMemberRef } from '../team.js';
import { Cents, Uuid } from './common.js';
import { SupportSettings } from './support.js';
import { DEFAULT_GMAIL_HISTORY_MONTHS, GmailHistorySettings } from './google.js';
import { DEFAULT_REVIEW_MIN_CENTS } from '../expenses.js';
import {
  ATTRIBUTION_KEYS,
  LEGACY_ATTRIBUTION_KEYS,
  type AttributionKey,
  type LegacyAttributionKey,
} from '../attribution.js';
import { MeetingSettings } from '../meetings.js';
import { R1BriefSettings } from './r1.js';
import { CoachSettings } from './coach.js';

/**
 * Valeurs de la table `settings` (CDC §3.2) posées par les données de démonstration du lot A.
 * Les écrans et les routes `GET/PUT /api/admin/settings/:key` viendront avec les lots suivants.
 */

const Days = z.int().min(0).max(365);

/** Lot N : jours avant la date de facturation d'un abonnement hors Stripe où « Facturer l'abonnement » arrive. */
export const DEFAULT_MANUAL_INVOICE_DAYS_BEFORE = 7;

/**
 * Lot Q : une facture échue depuis plus de N jours n'est plus comptée comme un encaissement dans la trésorerie
 * (elle est listée à part, « non comptée »). 60 jours par défaut.
 */
export const DEFAULT_CASHFLOW_OVERDUE_EXCLUDE_AFTER_DAYS = 60;

/** Lot X : rendez-vous après lequel « Envoyer le devis » arrive. */
export const QuoteToSendAfter = z.enum(['R1', 'R2']);
export type QuoteToSendAfter = z.infer<typeof QuoteToSendAfter>;
/** Lot X : ce après quoi « Demander l'avis Google » arrive : le R5 ou la mise en ligne du site. */
export const ReviewRequestAfter = z.enum(['R5', 'online']);
export type ReviewRequestAfter = z.infer<typeof ReviewRequestAfter>;
/** Lot X : jours après un R1 sans R2 ni devis où « Fixer le R2 » arrive. */
export const DEFAULT_R2_MISSING_DAYS = 7;
/** Lot BT : jours sans réponse après lesquels un mail qui attend une réponse se relance, sauf urgence écrite dedans. */
export const DEFAULT_MAIL_FOLLOWUP_DAYS = 7;

/** `settings.rules` : paramètres des règles v1 (CDC §3.5). */
export const RulesSettings = z.object({
  quoteFollowup: z.object({ days: Days, repeatDays: Days }),
  invoiceDeposit: z.object({
    days: Days,
    /** Sous ce montant, une facture unique plutôt qu'un acompte (maquette : Kiné Océane, 600 €). */
    singleBelowCents: Cents.min(0).optional(),
  }),
  invoiceBalance: z.object({ days: Days }),
  subscriptionSetup: z.object({ daysAfterR5: Days }),
  unpaidReminder: z.object({ days: Days, repeatDays: Days }),
  /**
   * Lot X : « Demander l'avis Google » N jours après le R5 (agenda, sinon projet) ou après la mise en ligne du
   * site (back). Valeur posée par la migration : R5, 7 jours.
   */
  reviewRequest: z.object({ days: Days, after: ReviewRequestAfter.default('R5') }),
  /**
   * Lot X : « Envoyer le devis » N jours après le R1 ou le R2 de l'agenda. Valeur posée par la migration (demande
   * d'Inès du 13/10/2026) : R2, 0 jour.
   */
  quoteToSend: z.object({ days: Days, after: QuoteToSendAfter.default('R2') }),
  /** Lot X : « Fixer le R2 » N jours après un R1 sans R2 ni devis (7 jours par défaut). */
  r2Missing: z.object({ days: Days }).default({ days: DEFAULT_R2_MISSING_DAYS }),
  /**
   * Lot AT : « Préparer le devis » N jours après le R1 (0 par défaut : dès que le R1 est passé). Valeur par défaut :
   * un réglage enregistré avant le lot AT reste valide.
   */
  quotePrepare: z.object({ days: Days }).default({ days: 0 }),
  /**
   * Lot BT : relance des mails qui attendent une réponse : activée, et délai normal. Claude ne peut que le raccourcir,
   * quand le mail dit lui-même que c'est urgent.
   */
  mailFollowup: z
    .object({ enabled: z.boolean(), days: Days })
    .default({ enabled: true, days: DEFAULT_MAIL_FOLLOWUP_DAYS }),
  deadline: z.object({ daysBefore: Days }),
  anniversary: z.object({ years: z.array(z.int().min(1)) }),
  projectPlan: z.object({ days: Days }),
  /**
   * Lot N : « Facturer l'abonnement » d'un abonnement hors Stripe, proposé `daysBefore` jours avant chaque date
   * de facturation, sauf délai propre à l'abonnement (`manual_subscriptions.reminder_lead_days`). Stocké en
   * jours, saisi en semaines dans les Réglages (1 semaine par défaut). Valeur par défaut : un réglage enregistré
   * avant le lot N reste valide.
   */
  manualSubscriptionInvoice: z
    .object({ daysBefore: Days })
    .default({ daysBefore: DEFAULT_MANUAL_INVOICE_DAYS_BEFORE }),
  /**
   * Lot Q : trésorerie prévisionnelle, factures en retard de plus de `excludeAfterDays` jours non comptées
   * (probablement irrécouvrables, listées à part avec un lien vers les rappels de paiement). Valeur par défaut :
   * un réglage enregistré avant le lot Q reste valide.
   */
  cashflowOverdue: z
    .object({ excludeAfterDays: Days })
    .default({ excludeAfterDays: DEFAULT_CASHFLOW_OVERDUE_EXCLUDE_AFTER_DAYS }),
  /**
   * Attribution par défaut des actions proposées (Réglages > Règles des actions). Lot AK : une personne par type
   * de carte (`ATTRIBUTION_KEYS`, `packages/shared/src/attribution.ts`) ; les trois anciens groupes (`billing`
   * facturation et impayés, `sales` relances et devis, `admin` abonnements-domaines-dépenses) restent lus comme
   * repli d'un type jamais réglé (clé absente), puis l'administrateur. `null` = l'administrateur choisi.
   */
  attribution: z
    .object(
      Object.fromEntries(
        [...LEGACY_ATTRIBUTION_KEYS, ...ATTRIBUTION_KEYS].map((key) => [key, Uuid.nullable().optional()]),
      ) as Record<LegacyAttributionKey | AttributionKey, z.ZodOptional<z.ZodNullable<typeof Uuid>>>,
    )
    .optional(),
});
export type RulesSettings = z.infer<typeof RulesSettings>;

export const DEFAULT_RULES: RulesSettings = {
  quoteFollowup: { days: 10, repeatDays: 10 },
  invoiceDeposit: { days: 2, singleBelowCents: 100_000 },
  invoiceBalance: { days: 0 },
  subscriptionSetup: { daysAfterR5: 0 },
  unpaidReminder: { days: 5, repeatDays: 7 },
  reviewRequest: { days: 7, after: 'R5' },
  quoteToSend: { days: 0, after: 'R2' },
  r2Missing: { days: DEFAULT_R2_MISSING_DAYS },
  quotePrepare: { days: 0 },
  mailFollowup: { enabled: true, days: DEFAULT_MAIL_FOLLOWUP_DAYS },
  deadline: { daysBefore: 30 },
  anniversary: { years: [1, 3, 5] },
  projectPlan: { days: 1 },
  manualSubscriptionInvoice: { daysBefore: DEFAULT_MANUAL_INVOICE_DAYS_BEFORE },
  cashflowOverdue: { excludeAfterDays: DEFAULT_CASHFLOW_OVERDUE_EXCLUDE_AFTER_DAYS },
};

/**
 * `settings.planning` : rythme d'intégration et durées (CDC §3.6). Lot K : durée d'intégration proposée par
 * défaut (`defaultIntegrationWeeks`, modifiable par projet) et longueur de la période pour fixer un rendez-vous
 * R3, R4 ou R5 (`meetingWindowDays`). Ces deux champs ont une valeur par défaut : un réglage enregistré avant le
 * lot K reste valide.
 */
export const PlanningSettings = z.object({
  capacity: z.object({ usual: z.int().min(1), max: z.int().min(1) }),
  durations: z.object({
    r3ToMockup: Days,
    mockupToR4: Days,
    r4ToFeedback: Days,
    feedbackToIntegration: Days,
  }),
  /** Lundis des semaines de fermeture. */
  closedWeeks: z.array(z.string()),
  /** Intégration proposée au simulateur et à « Préparer le projet » (une semaine de plus pour une boutique). */
  defaultIntegrationWeeks: z.int().min(1).max(3).default(DEFAULT_INTEGRATION_WEEKS),
  /** Jours de la période pour fixer un rendez-vous, à partir de la date prévue par le planning. */
  meetingWindowDays: z.int().min(1).max(60).default(DEFAULT_MEETING_WINDOW_DAYS),
  /** Lot AE : maquettes qui peuvent démarrer la même semaine (hors sous-traitance). */
  mockupsPerWeek: z.int().min(1).max(10).default(DEFAULT_MOCKUPS_PER_WEEK),
  /** Lot AE : écart maximum R3 → maquette, en semaines (le minimum est `durations.r3ToMockup`). */
  r3ToMockupMaxWeeks: z.int().min(1).max(26).default(DEFAULT_R3_TO_MOCKUP_MAX_WEEKS),
  /** Lot AL : projets partenaires, intégration au plus tôt N jours après la signature du devis (7 par défaut). */
  partnerIntegrationDelayDays: z.int().min(0).max(90).default(DEFAULT_PARTNER_INTEGRATION_DELAY_DAYS),
});
export type PlanningSettings = z.infer<typeof PlanningSettings>;

export const DEFAULT_PLANNING: PlanningSettings = {
  capacity: { usual: 2, max: 3 },
  durations: { r3ToMockup: 21, mockupToR4: 7, r4ToFeedback: 7, feedbackToIntegration: 14 },
  closedWeeks: ['2026-12-28'],
  defaultIntegrationWeeks: DEFAULT_INTEGRATION_WEEKS,
  meetingWindowDays: DEFAULT_MEETING_WINDOW_DAYS,
  mockupsPerWeek: DEFAULT_MOCKUPS_PER_WEEK,
  r3ToMockupMaxWeeks: DEFAULT_R3_TO_MOCKUP_MAX_WEEKS,
  partnerIntegrationDelayDays: DEFAULT_PARTNER_INTEGRATION_DELAY_DAYS,
};

/** `settings.roles` : qui fait les maquettes, les rendez-vous et l'intégration (identifiants des membres). */
export const RolesSettings = z.object({ mockup: Uuid, meetings: Uuid, integration: Uuid });
export type RolesSettings = z.infer<typeof RolesSettings>;

/** `settings.chat` : choix proposé en premier dans l'écran de validation. */
export const ChatSettings = z.object({ defaultTarget: z.enum(['dm', 'group']) });
export type ChatSettings = z.infer<typeof ChatSettings>;
export const DEFAULT_CHAT: ChatSettings = { defaultTarget: 'dm' };

/** Objectifs de CA mensuels en centimes HT, clé `AAAA-MM` (valeurs de départ de la table `objectives`). */
export const ObjectivesSettings = z.record(z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/), Cents);
export type ObjectivesSettings = z.infer<typeof ObjectivesSettings>;

/** Objectifs 2026 du Google Sheet (maquette, `SHEET.objectif`), en euros. */
const OBJECTIVES_2026_EUROS = [15000, 15000, 10000, 8800, 6800, 5000, 4000, 4000, 12500, 12000, 9500, 9000];
export const DEFAULT_OBJECTIVES_2026: ObjectivesSettings = Object.fromEntries(
  OBJECTIVES_2026_EUROS.map((euros, index) => [`2026-${String(index + 1).padStart(2, '0')}`, euros * 100]),
);

// ------------------------------------------------------------------ Lot B (données)

/**
 * Catégories de produits des devis (CDC lot B : vitrine, e-commerce, refonte, intégrations, boîtes mail,
 * divers). `abonnement` regroupe les lignes d'abonnement, exclues du « signé par produit ».
 */
export const ProductCategory = z.enum([
  'vitrine',
  'ecommerce',
  'refonte',
  'integrations',
  'mail',
  'divers',
  'abonnement',
]);
export type ProductCategory = z.infer<typeof ProductCategory>;

export const PRODUCT_CATEGORY_LABELS: Record<ProductCategory, string> = {
  vitrine: 'Site vitrine',
  ecommerce: 'Site e-commerce',
  refonte: 'Refontes, extensions',
  integrations: 'Intégrations, audits',
  mail: 'Boîtes mail',
  divers: 'Divers',
  abonnement: 'Abonnements',
};

/** Motif (expression régulière, sans tenir compte de la casse ni des accents) → catégorie. */
const PatternSource = z
  .string()
  .min(1)
  .max(200)
  .refine(
    (value) => {
      try {
        new RegExp(value, 'i');
        return true;
      } catch {
        return false;
      }
    },
    { message: 'Expression régulière invalide' },
  );

/**
 * `settings.productCategories` : correspondance nom du produit (ou libellé de la ligne) → catégorie.
 * Les règles sont essayées dans l'ordre ; la première qui correspond l'emporte, sinon `fallback`.
 */
export const ProductCategoriesSettings = z.object({
  rules: z.array(z.object({ pattern: PatternSource, category: ProductCategory })).max(100),
  fallback: ProductCategory,
});
export type ProductCategoriesSettings = z.infer<typeof ProductCategoriesSettings>;

export const DEFAULT_PRODUCT_CATEGORIES: ProductCategoriesSettings = {
  rules: [
    { pattern: 'abonnement|maintenance mensuelle', category: 'abonnement' },
    { pattern: 'e.?commerce|boutique|click.?(and|&).?collect', category: 'ecommerce' },
    { pattern: 'refonte|extension|evolution|mise a jour du site', category: 'refonte' },
    { pattern: 'integration|audit|\\bseo\\b|referencement|connecteur', category: 'integrations' },
    { pattern: 'boites? ?(e.?)?mails?|messagerie|adresses? (e.?)?mail', category: 'mail' },
    { pattern: 'vitrine|site internet|site web|creation de site|landing', category: 'vitrine' },
    { pattern: '\\b(optimum|premium|platin\\w*|sauvegarde)\\b', category: 'abonnement' },
  ],
  fallback: 'divers',
};

/**
 * `settings.invoiceKinds` : détection du type de facture (acompte, solde, unique, abonnement), cf.
 * `apps/server/src/sync/invoice-kind.ts`. Les motifs portent sur le libellé des lignes et le numéro ;
 * `overrides` force le type d'une facture par son numéro Axonaut.
 */
export const InvoiceKind = z.enum(['deposit', 'balance', 'single', 'subscription']);
export type InvoiceKind = z.infer<typeof InvoiceKind>;

export const InvoiceKindsSettings = z.object({
  depositPatterns: z.array(PatternSource).max(50),
  balancePatterns: z.array(PatternSource).max(50),
  subscriptionPatterns: z.array(PatternSource).max(50),
  /** Part minimale du montant du devis pour qu'une facture rattachée soit « unique » (0,99 = 99 %). */
  singleThreshold: z.number().min(0.5).max(1),
  overrides: z.record(z.string().min(1).max(100), InvoiceKind),
});
export type InvoiceKindsSettings = z.infer<typeof InvoiceKindsSettings>;

export const DEFAULT_INVOICE_KINDS: InvoiceKindsSettings = {
  depositPatterns: ['acompte', 'arrhes', 'deposit'],
  balancePatterns: ['solde', 'reste a payer', 'balance'],
  subscriptionPatterns: ['abonnement', 'optimum', 'premium', 'platin', 'sauvegarde', 'mensualite'],
  singleThreshold: 0.99,
  overrides: {},
};

/** `settings.cashflow` : solde bancaire saisi à la main (utilisé si Axonaut ne fournit pas de solde). */
export const CashflowSettings = z.object({
  bankBalanceCents: Cents.nullable(),
  /** Date de la saisie du solde (`AAAA-MM-JJ`). */
  bankBalanceOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
});
export type CashflowSettings = z.infer<typeof CashflowSettings>;
export const DEFAULT_CASHFLOW: CashflowSettings = { bankBalanceCents: null, bankBalanceOn: null };

/** `settings.axonautBank` : soldes des comptes bancaires lus par la synchro Axonaut (`/bank-accounts`). */
export const AxonautBankSettings = z.object({
  balanceCents: Cents,
  accounts: z.array(
    z.object({ id: z.string(), name: z.string(), balanceCents: Cents, updatedAt: z.string().nullable() }),
  ),
  syncedAt: z.string(),
});
export type AxonautBankSettings = z.infer<typeof AxonautBankSettings>;

/**
 * Clés de la table `settings`. Les objectifs ne sont plus un réglage : ils vivent dans la table
 * `objectives` (source de vérité, CDC §3.2) depuis le lot B.
 */
/**
 * Personne d'une étape du modèle : un rôle du planning (`role:mockup`, `role:meetings`, `role:integration`,
 * résolu avec `settings.roles`) ou l'adresse e-mail d'un membre.
 */
export const StepAssignee = z.union([
  z.enum(['role:mockup', 'role:meetings', 'role:integration']),
  z.email().transform((value) => value.toLowerCase()),
]);
export type StepAssignee = z.infer<typeof StepAssignee>;

/** Plafond des personnes à prévenir après une étape. */
export const MAX_STEP_NOTIFY = 10;

/**
 * Personnes à prévenir une fois l'étape cochée (lot M) : une liste, sans doublon. Ancien format accepté en
 * lecture : `null` (ou absent) → `[]`, une personne seule → `[personne]`.
 */
export const StepNotifyList = z
  .preprocess(
    (value) => (value == null ? [] : typeof value === 'string' ? [value] : value),
    z.array(StepAssignee).max(MAX_STEP_NOTIFY),
  )
  .transform((list) => [...new Set(list)]);
export type StepNotifyList = z.infer<typeof StepNotifyList>;

/** Une étape du modèle des projets (maquette : `CARD_STEPS`, Réglages > « Modèle d'étapes »). */
export const StepTemplateItem = z.object({
  label: z.string().trim().min(1).max(200),
  phase: z.enum(['R3', 'Maquette', 'Ajustements', 'R4', 'Retours', 'Integration', 'R5']),
  /** Qui la fait (la tâche arrive dans son « À faire »). */
  who: StepAssignee,
  /**
   * Qui prévenir une fois cochée (lot M : 0 à n personnes, rôles et/ou membres). Un modèle enregistré avant le
   * lot M (`notify: personne | null`) se relit `[personne]` ou `[]`.
   */
  notify: StepNotifyList,
  /** Message proposé à la personne prévenue (« le R3 est fait, la maquette peut commencer »). */
  message: z.string().trim().max(500),
  /** Libellé de la tâche « À faire ». */
  task: z.string().trim().max(200),
});
export type StepTemplateItem = z.infer<typeof StepTemplateItem>;

/**
 * `settings.stepTemplate` : modèle d'étapes des projets, dans l'ordre (au moins une étape). Lot M : les
 * étapes s'ajoutent, se renomment, se suppriment et se réordonnent dans Réglages ; les libellés sont uniques
 * (sans tenir compte de la casse ni des espaces autour).
 */
export const StepTemplateSettings = z
  .array(StepTemplateItem)
  .min(1, 'Le modèle garde au moins une étape.')
  .max(60)
  .superRefine((items, ctx) => {
    const seen = new Map<string, number>();
    items.forEach((item, index) => {
      const key = item.label.trim().toLocaleLowerCase('fr');
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: [index, 'label'],
          message: `Deux étapes s'appellent « ${item.label} ».`,
        });
      } else seen.set(key, index);
    });
  });
export type StepTemplateSettings = z.infer<typeof StepTemplateSettings>;

/** Lot AL : un projet partenaire n'a que l'intégration et le R5 (le reste est géré par le partenaire). */
export const PARTNER_STEP_PHASES = ['Integration', 'R5'] as const;

/** Lot AL : `settings.partnerStepTemplate` : même modèle, phases Intégration et R5 seulement. */
export const PartnerStepTemplateSettings = StepTemplateSettings.superRefine((items, ctx) => {
  items.forEach((item, index) => {
    if (!(PARTNER_STEP_PHASES as readonly string[]).includes(item.phase)) {
      ctx.addIssue({
        code: 'custom',
        path: [index, 'phase'],
        message: `Un projet partenaire n'a que l'intégration et le R5 (« ${item.label} »).`,
      });
    }
  });
});

/**
 * Personne du modèle par défaut (lot S) : un rôle du planning, ou une personne de l'équipe désignée par son
 * prénom (`team:ines`, `team:stephane`, `team:admin`), jamais une adresse écrite en dur. `resolveDefaultStepTemplate`
 * la remplace par l'adresse du membre correspondant au moment de la lecture.
 */
export type DefaultStepPerson = 'role:mockup' | 'role:meetings' | 'role:integration' | `team:${TeamKey}`;

export interface DefaultStepTemplateItem extends Omit<StepTemplateItem, 'who' | 'notify'> {
  who: DefaultStepPerson;
  notify: DefaultStepPerson[];
}

/**
 * Modèle par défaut = `CARD_STEPS` de la maquette (S = Inès, M = Maxime, P = Stéphane). Les étapes liées
 * aux rendez-vous, aux maquettes et à l'intégration suivent les rôles du planning ; les autres (facturation,
 * relecture, abonnement) visent une personne de l'équipe. Lot M (13/10/2026) : le client valide la maquette APRÈS
 * l'intégration de ses retours (« Maquette envoyée » → « Retours intégrés » → « Maquette validée » →
 * « Création du site ») ; les deux étapes sont dans la phase Retours (la validation clôt les retours, avant
 * l'intégration du planning).
 */
export const DEFAULT_STEP_TEMPLATE_SPEC: readonly DefaultStepTemplateItem[] = [
  {
    label: 'Acompte reçu',
    phase: 'R3',
    who: 'team:ines',
    notify: ['team:admin'],
    message: "l'acompte est reçu, le projet peut démarrer",
    task: "Vérifier l'acompte",
  },
  {
    label: 'Récupération des infos',
    phase: 'R3',
    who: 'team:ines',
    notify: [],
    message: '',
    task: 'Récupérer les infos du client',
  },
  {
    label: 'R3 fait',
    phase: 'R3',
    who: 'role:meetings',
    notify: ['role:mockup'],
    message: 'le R3 est fait, la maquette peut commencer',
    task: 'Faire le R3',
  },
  {
    label: 'Maquette envoyée',
    phase: 'Maquette',
    who: 'role:mockup',
    notify: ['role:meetings'],
    message: 'la maquette est envoyée au client, le R4 est à caler',
    task: 'Créer la maquette',
  },
  {
    label: 'Retours intégrés',
    phase: 'Retours',
    who: 'role:mockup',
    notify: ['role:meetings'],
    message: 'les retours sont intégrés, la maquette est à faire valider par le client',
    task: 'Intégrer les retours',
  },
  {
    label: 'Maquette validée',
    phase: 'Retours',
    who: 'role:meetings',
    notify: ['role:integration'],
    message: 'la maquette est validée par le client, le site peut être créé',
    task: 'Faire valider la maquette',
  },
  {
    label: 'Création du site',
    phase: 'Integration',
    who: 'role:integration',
    notify: ['team:stephane'],
    message: 'le site est monté, il reste la relecture et les tests',
    task: 'Créer le site',
  },
  {
    label: 'Relecture et tests',
    phase: 'Integration',
    who: 'team:stephane',
    notify: ['role:meetings'],
    message: 'relecture terminée, le R5 peut être calé',
    task: 'Relire et tester le site',
  },
  {
    label: 'R5 · formation',
    phase: 'R5',
    who: 'role:meetings',
    notify: [],
    message: '',
    task: 'Faire la formation (R5)',
  },
  {
    label: 'Mise en ligne',
    phase: 'R5',
    who: 'role:integration',
    notify: ['team:ines'],
    message: 'le site est en ligne, le solde est à facturer',
    task: 'Mettre le site en ligne',
  },
  {
    label: 'Solde reçu',
    phase: 'R5',
    who: 'team:ines',
    notify: ['team:stephane'],
    message: "le solde est reçu, l'abonnement peut être mis en place",
    task: 'Vérifier le solde',
  },
  {
    label: 'Abonnement en place',
    phase: 'R5',
    who: 'team:stephane',
    notify: [],
    message: '',
    task: "Mettre l'abonnement en place",
  },
];

/** Étapes communes à la fin des deux modèles (mise en ligne, solde, abonnement), reprises du modèle Evolusite. */
const COMMON_FINAL_STEP_LABELS = ['Mise en ligne', 'Solde reçu', 'Abonnement en place'] as const;

/**
 * Lot AL : modèle par défaut des projets PARTENAIRES (Evolusite n'est qu'intégrateur : le R3, la maquette, le R4
 * et les retours sont gérés par le partenaire). Acompte reçu (si besoin), Dossier photos, Création du site,
 * Relecture et tests, R5 · rendu du site, puis la même suite que nos projets (mise en ligne, solde, abonnement).
 * Un projet partenaire n'a que les phases Intégration et R5 : les étapes d'avant le site sont dans la phase
 * Intégration.
 */
export const DEFAULT_PARTNER_STEP_TEMPLATE_SPEC: readonly DefaultStepTemplateItem[] = [
  {
    label: 'Acompte reçu (si besoin)',
    phase: 'Integration',
    who: 'team:ines',
    notify: ['team:admin'],
    message: "l'acompte est reçu, l'intégration peut démarrer",
    task: "Vérifier l'acompte (si besoin)",
  },
  {
    label: 'Dossier photos',
    phase: 'Integration',
    who: 'role:meetings',
    notify: ['role:integration'],
    message: 'le dossier photos est arrivé, le site peut être créé',
    task: 'Récupérer le dossier photos',
  },
  {
    label: 'Création du site',
    phase: 'Integration',
    who: 'role:integration',
    notify: ['team:stephane'],
    message: 'le site est monté, il reste la relecture et les tests',
    task: 'Créer le site',
  },
  {
    label: 'Relecture et tests',
    phase: 'Integration',
    who: 'team:stephane',
    notify: ['role:meetings'],
    message: 'relecture terminée, le R5 peut être calé avec le partenaire et le client',
    task: 'Relire et tester le site',
  },
  {
    label: 'R5 · rendu du site',
    phase: 'R5',
    who: 'role:meetings',
    notify: [],
    message: '',
    task: 'Faire le R5 avec le partenaire et le client',
  },
  ...DEFAULT_STEP_TEMPLATE_SPEC.filter((item) =>
    (COMMON_FINAL_STEP_LABELS as readonly string[]).includes(item.label),
  ),
];

/**
 * Lot S : le modèle par défaut pour cette équipe. Chaque `team:*` devient l'adresse du membre retrouvé par son
 * prénom (puis son initiale ; l'administrateur actif pour `team:admin`). Personne de retrouvé : « Fait par »
 * revient à l'administrateur (sinon au rôle des rendez-vous) et la personne sort de « prévenir ».
 */
export function resolveDefaultStepTemplate(
  members: readonly TeamMemberRef[],
  spec: readonly DefaultStepTemplateItem[] = DEFAULT_STEP_TEMPLATE_SPEC,
): StepTemplateSettings {
  const cache = new Map<TeamKey, string | null>();
  const emailOf = (key: TeamKey): string | null => {
    if (!cache.has(key))
      cache.set(key, findTeamMember(TEAM_HINTS[key], members)?.email.toLowerCase() ?? null);
    return cache.get(key) ?? null;
  };
  const resolve = (person: DefaultStepPerson): StepAssignee | null =>
    person.startsWith('team:') ? emailOf(person.slice(5) as TeamKey) : (person as StepAssignee);
  return spec.map((item) => ({
    ...item,
    who: resolve(item.who) ?? emailOf('admin') ?? 'role:meetings',
    notify: [...new Set(item.notify.map(resolve).filter((p): p is StepAssignee => p !== null))],
  }));
}

/** Lot AL : le modèle « Projets partenaires » par défaut pour cette équipe (même résolution des personnes). */
export function resolveDefaultPartnerStepTemplate(members: readonly TeamMemberRef[]): StepTemplateSettings {
  return resolveDefaultStepTemplate(members, DEFAULT_PARTNER_STEP_TEMPLATE_SPEC);
}

/**
 * Le modèle par défaut résolu pour l'équipe de DÉMONSTRATION (`ines@`, `stephane@`, `mmagne@evolusite.fr`) :
 * données de démo et tests seulement. Le serveur résout le modèle avec les vrais membres.
 */
export const DEMO_TEAM: readonly TeamMemberRef[] = [
  { email: 'mmagne@evolusite.fr', name: 'Maxime', initials: 'M', role: 'ADMIN', status: 'ACTIVE' },
  { email: 'ines@evolusite.fr', name: 'Inès', initials: 'I', role: 'MEMBER', status: 'ACTIVE' },
  { email: 'stephane@evolusite.fr', name: 'Stéphane', initials: 'S', role: 'MEMBER', status: 'ACTIVE' },
];
export const DEMO_STEP_TEMPLATE: StepTemplateSettings = resolveDefaultStepTemplate(DEMO_TEAM);
/** Lot AL : le modèle « Projets partenaires » de l'équipe de démonstration. */
export const DEMO_PARTNER_STEP_TEMPLATE: StepTemplateSettings = resolveDefaultPartnerStepTemplate(DEMO_TEAM);

/** `settings.calendar` : reconnaissance des rendez-vous dans l'agenda (Réglages > Google Agenda). */
export const CalendarSettings = z.object({
  /** Un rendez-vous est un R1 si son titre commence par ce préfixe. */
  r1Prefix: z.string().trim().min(1).max(20),
});
export type CalendarSettings = z.infer<typeof CalendarSettings>;
export const DEFAULT_CALENDAR_SETTINGS: CalendarSettings = { r1Prefix: 'R1' };

/** Lot Y [16] : `settings.expenses` : seuil de « À vérifier » (300 € par défaut ; 0 = toutes les lignes). */
export const ExpensesSettings = z.object({ reviewMinCents: Cents.min(0).max(100_000_000) });
export type ExpensesSettings = z.infer<typeof ExpensesSettings>;
export const DEFAULT_EXPENSES_SETTINGS: ExpensesSettings = { reviewMinCents: DEFAULT_REVIEW_MIN_CENTS };

/**
 * Lot AG [16] : `settings.fiscalYear` : jour de fin d'exercice (`MM-JJ`, 31 décembre par défaut), pour la tuile
 * « Solde au <fin d'exercice> » de la Trésorerie. Un 29 février vaut le 28 les années non bissextiles.
 */
export const FiscalYearSettings = z.object({
  fiscalYearEnd: z
    .string()
    .regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, 'Fin d’exercice attendue au format MM-JJ')
    .refine((v) => {
      const [m, d] = v.split('-').map(Number) as [number, number];
      return d <= [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]!;
    }, 'Ce jour n’existe pas'),
});
export type FiscalYearSettings = z.infer<typeof FiscalYearSettings>;
export const DEFAULT_FISCAL_YEAR: FiscalYearSettings = { fiscalYearEnd: '12-31' };

/** Lot S : `settings.gmailHistory` par défaut (24 mois). */
export const DEFAULT_GMAIL_HISTORY: GmailHistorySettings = { months: DEFAULT_GMAIL_HISTORY_MONTHS };

export const SETTING_KEYS = [
  'rules',
  'planning',
  'roles',
  'chat',
  'productCategories',
  'invoiceKinds',
  'cashflow',
  'axonautBank',
  'stepTemplate',
  'calendar',
  'gmailHistory',
  'expenses',
  'fiscalYear',
  // Lot AF : titres des rendez-vous R1-R5 et invitation du client par défaut (Réglages > Projets).
  'meetings',
  // Lot AL : second modèle d'étapes, pour les projets partenaires (Réglages > Projets > Modèles d'étapes).
  'partnerStepTemplate',
  // Lots AN/AO : fiches R1 (Réglages > Projets > Fiches R1).
  'r1Briefs',
  // Lot AY : délai de paiement des sous-traitants (Réglages > Chiffres).
  'subcontracting',
  // Dernière lecture des fournisseurs et du catalogue par la synchro Axonaut (écrits par la synchro seulement).
  'axonautSuppliersRead',
  'axonautProductsRead',
  // Lot BB : boîtes Gmail lues pour le support et domaines des fournisseurs (Réglages > Support).
  'support',
  // Lot BF : coach de journée (plafond de rappels, seuil des cartes repoussées, bornes de la journée).
  'coach',
] as const;
export type SettingKey = (typeof SETTING_KEYS)[number];

/** Réglages écrits par la synchro Axonaut : `PUT /api/admin/settings/:key` les refuse. */
export const READ_ONLY_SETTING_KEYS: readonly SettingKey[] = [
  'axonautBank',
  'axonautSuppliersRead',
  'axonautProductsRead',
];

/** Schéma de chaque réglage (`GET/PUT /api/admin/settings/:key`). */
export const SETTING_SCHEMAS = {
  rules: RulesSettings,
  planning: PlanningSettings,
  roles: RolesSettings,
  chat: ChatSettings,
  productCategories: ProductCategoriesSettings,
  invoiceKinds: InvoiceKindsSettings,
  cashflow: CashflowSettings,
  axonautBank: AxonautBankSettings,
  stepTemplate: StepTemplateSettings,
  calendar: CalendarSettings,
  gmailHistory: GmailHistorySettings,
  expenses: ExpensesSettings,
  fiscalYear: FiscalYearSettings,
  meetings: MeetingSettings,
  partnerStepTemplate: PartnerStepTemplateSettings,
  r1Briefs: R1BriefSettings,
  subcontracting: SubcontractingSettings,
  axonautSuppliersRead: AxonautReadState,
  axonautProductsRead: AxonautReadState,
  support: SupportSettings,
  coach: CoachSettings,
} as const satisfies Record<SettingKey, z.ZodType>;

export const SettingKeyParams = z.object({ key: z.enum(SETTING_KEYS) });

/** Réponse de `GET/PUT /api/admin/settings/:key` (valeur par défaut si la clé n'est pas encore en base). */
export const SettingResponse = z.object({
  key: z.enum(SETTING_KEYS),
  value: z.unknown(),
  isDefault: z.boolean(),
});
export type SettingResponse = z.infer<typeof SettingResponse>;

/** Corps de `PUT /api/admin/settings/:key` : la valeur est validée par `SETTING_SCHEMAS[key]`. */
export const UpdateSettingBody = z.object({ value: z.unknown() });
export type UpdateSettingBody = z.infer<typeof UpdateSettingBody>;
